import { useEffect, useState } from 'react';
import { KeyRound, LoaderCircle, LockKeyhole } from 'lucide-react';
import { api } from '../api';

interface Props {
  onAuthenticated: () => Promise<void>;
}

export function LoginScreen({ onAuthenticated }: Props) {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [setupToken, setSetupToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void api.sessionStatus()
      .then((status) => setConfigured(status.configured))
      .catch(() => setError('無法連線到登入服務'));
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!password) return;
    if (!configured && password !== confirmation) {
      setError('兩次輸入的密碼不一致');
      return;
    }
    if (!configured && password.length < 8) {
      setError('密碼至少需要 8 個字元');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (configured) await api.login(password);
      else await api.setupPassword(setupToken.trim(), password);
      setPassword('');
      setConfirmation('');
      setSetupToken('');
      await onAuthenticated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '登入失敗');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-screen">
      <form className="login-panel" onSubmit={submit}>
        <img src="/icons/icon.svg" alt="" className="login-icon" />
        <div>
          <h1>備忘提醒</h1>
          <p>{configured === false ? '第一次使用，請建立登入密碼' : '輸入你的登入密碼'}</p>
        </div>
        {configured === false && (
          <label className="field">
            <span>初始設定碼</span>
            <span className="input-with-icon">
              <KeyRound size={19} aria-hidden="true" />
              <input
                type="password"
                autoComplete="one-time-code"
                value={setupToken}
                onChange={(event) => setSetupToken(event.target.value)}
                autoFocus
              />
            </span>
          </label>
        )}
        <label className="field">
          <span>{configured === false ? '新密碼' : '密碼'}</span>
          <span className="input-with-icon">
            <LockKeyhole size={19} aria-hidden="true" />
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoFocus={configured === true}
            />
          </span>
        </label>
        {configured === false && (
          <label className="field">
            <span>確認新密碼</span>
            <span className="input-with-icon">
              <LockKeyhole size={19} aria-hidden="true" />
              <input
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </span>
          </label>
        )}
        {error && <p className="form-error" role="alert">{error}</p>}
        <button
          className="primary-button login-button"
          type="submit"
          disabled={loading || configured == null || !password || (!configured && (!setupToken.trim() || !confirmation))}
        >
          {loading && <LoaderCircle className="spin" size={18} />}
          {configured === false ? '設定並登入' : '登入'}
        </button>
      </form>
    </main>
  );
}
